import React, { useEffect, useRef, useState, useCallback, useId } from "react";
import useGlobalVideoMute from "@/hooks/useGlobalVideoMute";
import DiagonalShimmer from "../loaders/DiagonalShimmer";
import MediaActions from "./actions";
import { Action, AdStatus } from "./types";
import { IconInfoCircle, IconPhotoOff } from "@tabler/icons-react";
import LoadingSpinner from "../loaders/LoadingSpinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";
import { VideoRetentionData } from "@/screens/creatives/types";
import VideoControls from "./VideoControls";
import VideoRetentionGraph from "./VideoRetentionGraph";

// Custom event name for video playback coordination
const VIDEO_PLAY_EVENT = "media-video-play";

type MediaType = "video" | "image"; // can't be mixed because this will render either image or video only one thing
export interface MediaProps {
  type: MediaType;
  source?: string;
  ad_id?: string;
  status?: AdStatus;
  thumbnail?: string;
  isNew?: boolean;
  onClick?: () => void;
  actions?: Action[];
  original_source?: string;
  isPolling?: boolean;
  crossOrigin?: React.ImgHTMLAttributes<HTMLImageElement>["crossOrigin"];
  className?: string;
  aspectRatio?: string;
  forcedAspectRatio?: string;
  minLoadingHeight?: number;
  videoRetentionData?: VideoRetentionData;
  containerWidth?: number;
}

const imageKey = 1754068657946; // Static key for image re-rendering

export const withCacheBuster = (url: string, buster?: string) => {
  if (url.startsWith("blob:")) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}cb=${buster ? buster : 1754068657946}`;
};

// SessionStorage keys for video timestamps
const VIDEO_TIMESTAMPS_KEY = "video_timestamps";
const VIDEO_TIMESTAMPS_ORDER_KEY = "video_timestamps_order";

// Helper to create a unique key from ad_id and url
const createVideoTimestampKey = (url: string, adId?: string): string => {
  // Remove cache buster from URL for consistent key
  const cleanUrl = url.split(/[?&]/)[0];
  return adId ? `${adId}:${cleanUrl}` : cleanUrl;
};

// Helper to manage video timestamps in sessionStorage (max 10 entries)
const getVideoTimestamps = (): Record<string, number> => {
  try {
    const stored = sessionStorage.getItem(VIDEO_TIMESTAMPS_KEY);
    return stored ? (JSON.parse(stored) as Record<string, number>) : {};
  } catch {
    return {};
  }
};

const getVideoTimestampsOrder = (): string[] => {
  try {
    const stored = sessionStorage.getItem(VIDEO_TIMESTAMPS_ORDER_KEY);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
};

const saveVideoTimestamp = (url: string, timestamp: number, adId?: string): void => {
  try {
    const timestamps = getVideoTimestamps();
    const order = getVideoTimestampsOrder();

    const key = createVideoTimestampKey(url, adId);

    // Remove key from order if it exists (to move it to end)
    const existingIndex = order.indexOf(key);
    if (existingIndex !== -1) {
      order.splice(existingIndex, 1);
    }

    // Add key to end of order (most recent)
    order.push(key);

    // Update or add the timestamp
    timestamps[key] = timestamp;

    // Keep only the last 10 entries (LRU - remove oldest if needed)
    if (order.length > 10) {
      // Remove oldest entries
      const toRemove = order.splice(0, order.length - 10);
      toRemove.forEach((keyToRemove) => {
        delete timestamps[keyToRemove];
      });
    }

    sessionStorage.setItem(VIDEO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
    sessionStorage.setItem(VIDEO_TIMESTAMPS_ORDER_KEY, JSON.stringify(order));
  } catch (error) {
    console.warn("Failed to save video timestamp:", error);
  }
};

const getVideoTimestamp = (url: string, adId?: string): number | null => {
  try {
    const timestamps = getVideoTimestamps();
    const key = createVideoTimestampKey(url, adId);
    return timestamps[key] ?? null;
  } catch {
    return null;
  }
};

const clearVideoTimestamp = (url: string, adId?: string): void => {
  try {
    const timestamps = getVideoTimestamps();
    const order = getVideoTimestampsOrder();

    const key = createVideoTimestampKey(url, adId);

    // Remove from timestamps
    delete timestamps[key];

    // Remove from order
    const existingIndex = order.indexOf(key);
    if (existingIndex !== -1) {
      order.splice(existingIndex, 1);
    }

    sessionStorage.setItem(VIDEO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
    sessionStorage.setItem(VIDEO_TIMESTAMPS_ORDER_KEY, JSON.stringify(order));
  } catch (error) {
    console.warn("Failed to clear video timestamp:", error);
  }
};

// Helper function to calculate height based on aspect ratio
const calculateHeightFromAspectRatio = (
  aspectRatio?: string,
  containerWidth: number = 278,
): number | null => {
  if (!aspectRatio) return null; // Default height

  const [width, height] = aspectRatio.split(":").map(Number);
  if (!width || !height) return null; // Default height if parsing fails

  return Math.round((containerWidth * height) / width);
};

const MediaUnavailable = () => (
  <div className="absolute inset-0 flex items-center justify-center flex-col gap-4">
    <IconPhotoOff size={30} className="text-[#AAAAAA]" />
    <span className="font-medium text-sm text-center text-[#A5A5A5]">
      Preview Unavailable
    </span>
  </div>
);

const PlayButton = () => (
  <svg
    width="54"
    height="54"
    viewBox="0 0 54 54"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="transition-all duration-200 ease-in-out hover:scale-110 hover:opacity-100 opacity-90"
  >
    <rect width="53.6" height="53.6" rx="26.8" fill="black" fillOpacity={0.6} />
    <g>
      <path
        d="M21.5996 19.2344L32.7996 26.4344L21.5996 33.6344V19.2344Z"
        fill="white"
        stroke="white"
        strokeWidth="1.12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const Media = ({
  type,
  source,
  ad_id,
  thumbnail,
  onClick,
  status,
  actions = [],
  isNew,
  original_source,
  processingMsg,
  processingText,
  crossOrigin,
  className,
  aspectRatio,
  forcedAspectRatio,
  minLoadingHeight,
  videoRetentionData,
  containerWidth,
}: MediaProps & {
  processingText?: boolean;
  processingMsg?: string;
}) => {
  // Unique ID for this Media instance to coordinate video playback
  const mediaId = useId();
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Media state management
  const [currentSource, setCurrentSource] = useState<string | undefined>(source);
  const [currentType, setCurrentType] = useState<MediaType>(type);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasMediaError, setHasMediaError] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showOriginalSource, setShowOriginalSource] = useState<boolean>(false);
  const [videoProgress, setVideoProgress] = useState(0); // 0-100 percentage
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [imageHeight, setImageHeight] = useState<number | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [calculatedHeight, setCalculatedHeight] = useState<number | null>(null);
  const minHeight = minLoadingHeight ?? 372;
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const hasRestoredTimestampRef = useRef<boolean>(false);

  // Use forcedAspectRatio if provided, otherwise fall back to aspectRatio prop
  const effectiveAspectRatio = forcedAspectRatio || aspectRatio;

  // When source changes, the component may remount and mediaContainerRef.current is null during render.
  // Use only the helper's default width (278) here so we never read ref during render; the effect will set calculatedHeight once the ref is attached.
  const displayHeight =
    calculatedHeight ??
    (effectiveAspectRatio ? calculateHeightFromAspectRatio(effectiveAspectRatio, containerWidth) : null);
    (effectiveAspectRatio ? calculateHeightFromAspectRatio(effectiveAspectRatio, containerWidth) : null);

  // Convert aspect ratio from "4:5" format to "4/5" format for CSS
  const cssAspectRatio = effectiveAspectRatio
    ? effectiveAspectRatio.replace(":", "/")
    : undefined;

  // Global mute state via reusable hook
  const { isMuted, setMuted: setGlobalMuted } = useGlobalVideoMute();

  // Append cache-buster only for non-blob URLs

  // Image preloading state
  const [isPreloadingImage, setIsPreloadingImage] = useState(false);

  // Loading state management for different scenarios
  const [isPendingToSuccessTransition, setIsPendingToSuccessTransition] = useState(false);
  const [isSuccessToSuccessTransition, setIsSuccessToSuccessTransition] = useState(false);

  // Track previous values for transition detection
  const prevSourceRef = useRef<string | undefined>(source);
  const prevTypeRef = useRef<MediaType>(type);
  const prevStatusRef = useRef<AdStatus | undefined>(status);

  // Handle source, type, and status changes
  useEffect(() => {
    const sourceChanged = prevSourceRef.current !== source;
    const typeChanged = prevTypeRef.current !== type;
    const statusChanged = prevStatusRef.current !== status;

    // Reset error state on any change
    setHasMediaError(false);
    setIsPlaying(false);
    // Calculate image height for dynamic container sizing (when aspect ratio is not provided)
    if (!effectiveAspectRatio) {
      setImageHeight(null);
    }

    // Handle type changes
    if (typeChanged) {
      setCurrentType(type);

      // Reset video if switching from video to image
      if (prevTypeRef.current === "video" && videoRef.current) {
        videoRef.current.currentTime = 0;
        void videoRef.current.pause();
      }
    }

    // Handle source changes
    if (sourceChanged && source) {
      // Determine loading state based on status transition
      if (status === "success" && prevStatusRef.current === "pending") {
        // Case 1: Pending to Success transition
        setIsPendingToSuccessTransition(true);
        setIsSuccessToSuccessTransition(false);
      } else if (status === "success" && prevStatusRef.current === "success") {
        // Case 2: Success to Success transition (different source)
        setIsPendingToSuccessTransition(false);
        setIsSuccessToSuccessTransition(true);
      } else {
        // Other transitions (pending to pending, etc.)
        setIsPendingToSuccessTransition(false);
        setIsSuccessToSuccessTransition(false);
      }

      // For pending states or pending-to-success transitions, preload the image
      if (
        status === "pending" ||
        (status === "success" && prevStatusRef.current === "pending")
      ) {
        // Only use image preloading for images, not videos
        // Videos handle their own loading via thumbnail/video elements
        if (type === "video") {
          setCurrentSource(source);
          // Don't set isPreloadingImage for videos - they don't use the hidden preloader
        } else {
          setIsPreloadingImage(true);
          // Don't change currentSource yet - keep the old image visible
          // The hidden image element will handle the preloading
        }
      } else {
        // For other cases, set the source directly
        setCurrentSource(source);
      }
    } else if (sourceChanged && !source) {
      // Source was cleared
      setCurrentSource(undefined);
      setIsPreloadingImage(false);
    }

    // Handle status changes without source change
    if (statusChanged && !sourceChanged) {
      if (status === "success" && prevStatusRef.current === "pending") {
        setIsMediaLoading(false);
        setIsMediaLoaded(true);
        setIsPendingToSuccessTransition(false);
        setIsSuccessToSuccessTransition(false);
      }
    }

    // Update refs
    prevSourceRef.current = source;
    prevTypeRef.current = type;
    prevStatusRef.current = status;

    return () => {
      setIsPendingToSuccessTransition(false);
      setIsSuccessToSuccessTransition(false);
      setIsPreloadingImage(false);
    };
  }, [source, type, status, effectiveAspectRatio]);

  // Calculate height from aspect ratio once ref is available and on resize
  useEffect(() => {
    const container = mediaContainerRef.current;
    if (!container || !effectiveAspectRatio) {
      return;
    }

    const calculateHeight = () => {
      const width = container.clientWidth;
      if (width > 0) {
        const height = calculateHeightFromAspectRatio(effectiveAspectRatio, width);
        setCalculatedHeight(height);
      }
    };

    // Calculate initial height
    calculateHeight();

    // Use ResizeObserver to recalculate on container resize
    const resizeObserver = new ResizeObserver(() => {
      calculateHeight();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [effectiveAspectRatio]);

  // Keep the actual video element in sync with isMuted changes (always, so play respects muted state)
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isPlaying]);

  // Restore video timestamp when metadata is loaded and we're about to play
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !source) return;

    const restoreTimestamp = () => {
      const savedTimestamp = getVideoTimestamp(source, ad_id);
      if (savedTimestamp !== null && savedTimestamp > 0) {
        // Only restore if video is at the beginning (within 0.5 seconds)
        // This prevents overwriting user seeks
        if (
          Math.abs(video.currentTime) < 0.1 &&
          Math.abs(savedTimestamp - video.duration) > 0.1 &&
          !hasRestoredTimestampRef.current
        ) {
          video.currentTime = savedTimestamp;
          hasRestoredTimestampRef.current = true;
        }
      }
    };

    const handleLoadedMetadata = () => {
      // Small delay to ensure video is ready
      setTimeout(() => {
        if (isPlaying && video.readyState >= 2) {
          // HAVE_CURRENT_DATA or higher
          restoreTimestamp();
        }
      }, 50);
    };

    // If metadata is already loaded and we're playing, restore immediately
    if (video.readyState >= 2 && isPlaying) {
      // HAVE_CURRENT_DATA or higher
      restoreTimestamp();
    }

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("canplay", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("canplay", handleLoadedMetadata);
    };
  }, [source, isPlaying, videoRef, ad_id]);

  // Track video time and progress (shared listener for controls and retention graph)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setVideoCurrentTime(video.currentTime);
      if (video.duration > 0) {
        const progress = (video.currentTime / video.duration) * 100;
        setVideoProgress(progress);
      }
    };

    const handleDurationChange = () => {
      setVideoDuration(video.duration || 0);
    };

    const handleVideoEnded = () => {
      // Reset timestamp when video ends so it starts from beginning next time
      if (source) {
        clearVideoTimestamp(source, ad_id);
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleDurationChange);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("ended", handleVideoEnded);

    // Initialize duration if already loaded
    if (video.duration) {
      setVideoDuration(video.duration);
    }

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleDurationChange);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("ended", handleVideoEnded);
    };
  }, [isPlaying, videoRef, source, ad_id]);

  // Track video buffering state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleWaiting = () => {
      // Video is buffering - show loader
      setIsBuffering(true);
    };

    const handleCanPlay = () => {
      // Video has enough data to play - hide loader
      setIsBuffering(false);
    };

    const handlePlaying = () => {
      // Video started playing - hide loader
      setIsBuffering(false);
    };

    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("playing", handlePlaying);

    return () => {
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("playing", handlePlaying);
    };
  }, [videoRef, isPlaying]);

  // Initialize mute state from hook and keep element in sync handled below

  // Listen for other videos playing and pause this one to reduce memory consumption
  // When isPlaying is set to false, the video tag unmounts and thumbnail is shown instead
  useEffect(() => {
    const handleOtherVideoPlay = (event: Event) => {
      const customEvent = event as CustomEvent<{ id: string }>;
      if (customEvent.detail.id !== mediaId && isPlaying) {
        // Another video started playing, pause this one
        if (videoRef.current && source) {
          // Save current timestamp before pausing
          const currentTime = videoRef.current.currentTime;
          if (currentTime > 0) {
            saveVideoTimestamp(source, currentTime, ad_id);
            hasRestoredTimestampRef.current = false;
          }
          videoRef.current.pause();
          // Don't reset currentTime to 0 - keep it for when video resumes
        }
        setIsPlaying(false);
        setVideoProgress(0);
        setVideoCurrentTime(0);
      }
    };

    window.addEventListener(VIDEO_PLAY_EVENT, handleOtherVideoPlay);
    return () => {
      window.removeEventListener(VIDEO_PLAY_EVENT, handleOtherVideoPlay);
    };
  }, [mediaId, isPlaying, source, ad_id]);

  const handleVideoPlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Notify other videos to pause (this will cause them to unmount their video tags)
    window.dispatchEvent(new CustomEvent(VIDEO_PLAY_EVENT, { detail: { id: mediaId } }));
    setIsPlaying(true);
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
      void video.play();
    }
    // Timestamp will be restored by useEffect when metadata loads
  };

  const handleVideoPlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      // Notify other videos to pause before playing this one
      window.dispatchEvent(
        new CustomEvent(VIDEO_PLAY_EVENT, { detail: { id: mediaId } }),
      );
      video.muted = isMuted;
      void video.play();
      setIsPlaying(true);
      // Timestamp will be restored by useEffect when metadata loads
    } else {
      video.pause();
      // setIsPlaying(false);
    }
  }, [mediaId, isMuted]);

  const handleMuteToggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const newMutedState = !isMuted;
    video.muted = newMutedState;
    setGlobalMuted(newMutedState);
  }, [isMuted, setGlobalMuted]);

  const handleMediaLoad = () => {
    setIsMediaLoading(false);
    setIsMediaLoaded(true);
    setIsPendingToSuccessTransition(false);
    setIsSuccessToSuccessTransition(false);

    // Calculate image height for dynamic container sizing (when aspect ratio is not provided)
    if (!effectiveAspectRatio && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      setImageHeight(rect.height);
    }

    // If we clicked play on thumbnail, start playing now that video is loaded
    if (isPlaying && videoRef.current && currentType === "video") {
      void videoRef.current.play();
      // Timestamp will be restored by useEffect when metadata loads
    }
  };

  const handleImageError = (error: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = error.target as HTMLImageElement;

    console.warn("Image load error:", {
      src: target.src,
      crossOrigin: target.crossOrigin,
    });

    // Always set error state since we're using CORS for all images
    setHasMediaError(true);
    setIsMediaLoading(false);
    setIsMediaLoaded(false);
    setIsPendingToSuccessTransition(false);
    setIsSuccessToSuccessTransition(false);
    setImageHeight(null);
  };

  const handleMediaError = () => {
    setHasMediaError(true);
    setIsMediaLoading(false);
    setIsMediaLoaded(false);
    setIsPendingToSuccessTransition(false);
    setIsSuccessToSuccessTransition(false);
    setImageHeight(null);
  };

  const handleImageRightClick = () => {
    posthog.capture("ad_image_right_clicked", {
      media_url: source,
      creative_type: type,
      aspect_ratio: aspectRatio,
    });
  };

  // When user toggles mute/unmute via native controls, propagate to session
  const handleVolumeChange = () => {
    const el = videoRef.current;
    if (!el) return;
    const nextMuted = el.muted || el.volume === 0;
    if (nextMuted !== isMuted) setGlobalMuted(nextMuted);
  };

  // Determine which loading state to show
  const shouldShowPendingToSuccessShimmer =
    isPendingToSuccessTransition && status === "success";
  const shouldShowSuccessToSuccessShimmer =
    isSuccessToSuccessTransition && status === "success";
  const shouldShowPendingShimmer = status === "pending" && source;
  const shouldShowPreloadingShimmer = isPreloadingImage;
  const shouldShowGeneralLoading =
    isMediaLoading &&
    !shouldShowPendingToSuccessShimmer &&
    !shouldShowSuccessToSuccessShimmer &&
    !shouldShowPendingShimmer &&
    !shouldShowPreloadingShimmer;

  // Only show full-screen shimmer when there's no source at all
  if (status === "pending" && !source && !currentSource)
    return (
      <div
        ref={mediaContainerRef}
        style={{
          minHeight: displayHeight ? `${displayHeight}px` : minHeight,
          ...(displayHeight && {
            height: displayHeight,
          }),
          ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          "group z-0 w-full overflow-hidden relative bg-[#FFFFFF] rounded-[12px]",
          className,
        )}
      >
        <div className="z-0">
          <DiagonalShimmer
            duration={6}
            colors={["#DEFFF670", "#D4F3FF70", "#FFE4F070", "#FFECE670"]}
          />
        </div>
        <div className="z-10">
          <MediaActions
            actions={actions}
            isMediaLoaded={isMediaLoaded}
            isMediaHovered={isHovered}
            isMediaPlaying={isPlaying}
            isReady={false}
          />
        </div>
      </div>
    );

  // Show error state only when there's no source and no current source
  if ((!source && !currentSource) || hasMediaError) {
    return (
      <div
        ref={mediaContainerRef}
        style={{
          minHeight: displayHeight ? `${displayHeight}px` : minHeight,
          ...(displayHeight && {
            height: displayHeight,
          }),
          ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          "group z-0 w-full overflow-hidden relative bg-[#F6F6F6] rounded-[12px]",
          className,
        )}
      >
        <div className="z-0">
          <MediaUnavailable />
        </div>
        <div className="z-10">
          <MediaActions
            actions={actions}
            isMediaLoaded={isMediaLoaded}
            isMediaHovered={isHovered}
            isMediaPlaying={isPlaying}
            isReady={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mediaContainerRef}
      className={cn(
        "group w-full relative bg-[#F6F6F6] overflow-hidden rounded-[12px]",
        currentType === "video" || onClick ? "cursor-pointer" : "cursor-default",
        className,
      )}
      style={{
        minHeight: displayHeight
          ? `${displayHeight}px`
          : imageHeight
            ? `${imageHeight}px`
            : isMediaLoading
              ? `${minHeight}px`
              : "0px",
        ...(imageHeight &&
          !displayHeight && {
            height: imageHeight,
          }),
        ...(displayHeight && {
          height: displayHeight,
          ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
        }),
      }}
      onClick={
        status === "success" &&
        !isPendingToSuccessTransition &&
        !isSuccessToSuccessTransition &&
        !shouldShowPendingShimmer &&
        !shouldShowPreloadingShimmer &&
        !isPlaying
          ? onClick
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {processingText ? (
        <div className="absolute inset-0 flex flex-col gap-2 items-center justify-center h-full w-full bg-black/40 backdrop-blur-[2px]">
          <LoadingSpinner size={24} className="text-white animate-spin-slow" />
          <span className="text-[12px] font-[600] text-white/90 text-center max-w-[90%]">
            {processingMsg}
          </span>
        </div>
      ) : (
        <>
          {/* Combined shimmer for preloading, pending, and pending-to-success transitions */}
          {(shouldShowPreloadingShimmer ||
            shouldShowPendingShimmer ||
            shouldShowPendingToSuccessShimmer) && (
            <DiagonalShimmer
              key="preloading-pending-transition"
              backgroundSize="400% 200%"
              duration={5}
              gradient="linear-gradient(-45deg, rgba(255,91,26,0) 0%, rgba(255,91,26,0) 35%, #FF5B1A33 49%, #FF517333 51%, rgba(255,81,115,0) 65%, rgba(255,81,115,0) 100%)"
            />
          )}

          {/* Combined shimmer for success-to-success transitions and general loading */}
          {(shouldShowSuccessToSuccessShimmer || shouldShowGeneralLoading) && (
            <DiagonalShimmer
              key="success-transition-general"
              colors={["#F6F6F6", "#BFBFBF", "#FFFFFF"]}
              duration={3}
            />
          )}
        </>
      )}

      {currentType === "video" ? (
        <>
          {/* Mount video on hover (preload) or when playing / no thumbnail */}
          {(!thumbnail || isHovered || isPlaying) && (
            <>
              <video
                key={`video-${source}-${imageKey}`}
                ref={videoRef}
                src={source}
                preload="auto"
                muted={isMuted}
                style={{
                  minHeight: displayHeight
                    ? `${displayHeight}px`
                    : imageHeight
                      ? `${imageHeight}px`
                      : `${minHeight}px`,
                  ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
                }}
                className={cn(
                  "w-full h-full",
                  forcedAspectRatio ? "object-contain bg-black" : "object-contain",
                  thumbnail && !isPlaying
                    ? "opacity-0 pointer-events-none absolute inset-0"
                    : cn(
                        isMediaLoading ? "opacity-0" : "opacity-100",
                        "transition-opacity duration-300",
                      ),
                )}
                playsInline
                onError={handleMediaError}
                onLoadedData={handleMediaLoad}
                onLoadStart={() => {
                  if (!thumbnail || isPlaying) setIsMediaLoading(true);
                }}
                onVolumeChange={handleVolumeChange}
                onClick={(e) => {
                  e.stopPropagation();
                  handleVideoPlayPause();
                }}
              />

              {/* Video Retention Graph - shown when video has been played and hovered */}
              {(isPlaying || !thumbnail) &&
                videoRetentionData &&
                videoRetentionData.length > 0 && (
                  <VideoRetentionGraph
                    data={videoRetentionData}
                    isVisible={isMediaLoaded && isPlaying}
                    isHovered={isHovered}
                    videoProgress={videoProgress}
                  />
                )}

              {/* Custom Video Controls - only when video is visible (playing or no thumbnail) */}
              {(isPlaying || !thumbnail) && (
                <VideoControls
                  videoRef={videoRef}
                  onPlayPause={handleVideoPlayPause}
                  isMuted={isMuted}
                  onMuteToggle={handleMuteToggle}
                  showControls={isMediaLoaded}
                  isHovered={isHovered}
                  currentTime={videoCurrentTime}
                  duration={videoDuration}
                  videoRetentionData={videoRetentionData}
                />
              )}

              {/* Buffering loader - only when video is visible */}
              {(isPlaying || !thumbnail) && isBuffering && isMediaLoaded && (
                <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
                  <LoadingSpinner
                    size={64}
                    strokeWidth={1.5}
                    className="text-black animate-spin-slow"
                  />
                </div>
              )}

              {/* Play overlay when video visible, loaded, and paused */}
              {(isPlaying || !thumbnail) && !isPlaying && isMediaLoaded && (
                <div
                  role="button"
                  className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
                >
                  <div onClick={handleVideoPlay} className="pointer-events-auto">
                    <PlayButton />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Thumbnail + play button when thumbnail available and not playing */}
          {thumbnail && !isPlaying && (
            <>
              <img
                ref={imageRef}
                key={`thumbnail-${thumbnail}-${imageKey}`}
                src={withCacheBuster(thumbnail)}
                alt="Video Thumbnail"
                className={cn(
                  "w-full h-full",
                  forcedAspectRatio ? "object-contain bg-black" : "object-contain",
                  isMediaLoading && !isHovered ? "opacity-0" : "opacity-100",
                  "transition-opacity duration-300",
                )}
                style={{
                  ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
                }}
                width="300"
                height="400"
                onError={handleImageError}
                onLoad={handleMediaLoad}
                onLoadStart={() => setIsMediaLoading(true)}
                loading="lazy"
              />
              <div
                role="button"
                className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
              >
                <div onClick={handleVideoPlay} className="pointer-events-auto">
                  <PlayButton />
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {status === "success" &&
            !isPendingToSuccessTransition &&
            !isSuccessToSuccessTransition &&
            !shouldShowPendingShimmer &&
            !shouldShowPreloadingShimmer &&
            original_source && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className={`absolute top-2 left-2 z-10 pointer-events-auto bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] font-['Helvetica_Neue'] text-[12px] font-normal leading-[16px] text-[#fff] p-[6px] rounded-[8px] transition-opacity cursor-pointer`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowOriginalSource(true);
                      }}
                      onMouseUp={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowOriginalSource(false);
                      }}
                      onMouseLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowOriginalSource(false);
                      }}
                    >
                      <IconInfoCircle size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipPrimitive.Portal>
                    <TooltipContent align="start" alignOffset={25}>
                      Show Original
                    </TooltipContent>
                  </TooltipPrimitive.Portal>
                </Tooltip>
              </TooltipProvider>
            )}
          {original_source && (
            <img
              key={`original-${original_source}-${imageKey}`}
              {...(crossOrigin && { crossOrigin })}
              src={withCacheBuster(original_source)}
              alt="Original Creative Image"
              className={cn(
                "absolute inset-0 w-full h-full transition-opacity duration-300 pointer-events-none",
                effectiveAspectRatio ? "object-contain bg-black" : "object-contain",
                showOriginalSource ? "opacity-100" : "opacity-0",
              )}
              style={{
                ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
              }}
              loading="lazy"
            />
          )}

          {/* Hidden preloading image - loads new image while keeping current image visible */}
          {isPreloadingImage && source && source !== currentSource && (
            <img
              key={`preload-${source}-${imageKey}`}
              src={withCacheBuster(source)}
              alt=""
              className={cn(
                "absolute inset-0 w-full h-full opacity-0 pointer-events-none",
                effectiveAspectRatio ? "object-contain" : "object-contain",
              )}
              style={{
                ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
              }}
              decoding="async"
              {...(crossOrigin && { crossOrigin })}
              onLoad={() => {
                // Image preloaded successfully, now set it as current source
                setCurrentSource(source);
                setIsPreloadingImage(false);
                setIsPendingToSuccessTransition(false);
              }}
              onError={() => {
                console.warn("Image preload failed:", source);
                // Still set the source and let the img element handle loading
                setCurrentSource(source);
                setIsPreloadingImage(false);
              }}
            />
          )}

          {currentSource && (
            <img
              ref={imageRef}
              key={`main-${currentSource}-${imageKey}`}
              {...(crossOrigin && { crossOrigin })}
              src={withCacheBuster(currentSource)}
              alt="Creative Image"
              className={cn(
                "w-full h-full",
                forcedAspectRatio ? "object-contain bg-black" : "object-contain",
                showOriginalSource ? "opacity-0" : "opacity-100",
                "transition-opacity duration-300",
              )}
              style={{
                ...(cssAspectRatio && { aspectRatio: cssAspectRatio }),
              }}
              onError={handleImageError}
              onLoad={handleMediaLoad}
              onLoadStart={() => setIsMediaLoading(true)}
              width="300"
              height="400"
              loading="lazy"
              decoding="sync"
              onContextMenu={handleImageRightClick}
            />
          )}
        </>
      )}
      {isNew && (
        <div className="absolute h-[30px] top-[13px] left-[13px] flex items-center justify-center bg-brand-orange/80 px-[14px] rounded-[8px]">
          <span className="text-[12px] font-normal leading-[19.157px] tracking-[0.24px] text-white">
            New
          </span>
        </div>
      )}
      {
        <>
          <MediaActions
            actions={actions}
            isMediaLoaded={isMediaLoaded}
            isMediaHovered={isHovered}
            isMediaPlaying={isPlaying}
            isReady={
              status === "success" &&
              !isPendingToSuccessTransition &&
              !isSuccessToSuccessTransition &&
              !shouldShowPendingShimmer &&
              !shouldShowPreloadingShimmer
            }
          />
        </>
      }
    </div>
  );
};

export default Media;
